import axios from 'axios'
import Cookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'

/**
 * Generates a device ID for the JE user to satisfy SmartGateway
 *  - `je-auser` cookie is present e.g. user visited CW
 *  - otherwise, generates a random UUID
 * @param {string} jeUserCookie - The je-auser cookie value
 * @returns {string} The device ID
 * */
function generateJeDeviceId(jeUserCookie) {
  let cookieValue = jeUserCookie
  const hasValidCookieValue = jeUserCookie && typeof jeUserCookie === 'string'
  if (!hasValidCookieValue) {
    const userCookieName = process.env.VUE_APP_JE_AUSER_COOKIE || 'je-auser'
    cookieValue = `${uuidv4()}`
    // To satisfy SmartGateway - generate a random uuid
    Cookies.set(userCookieName, cookieValue)
  }
  return cookieValue
}

/**
 * Default auth interface for CHAPI client auth methods.
 *  - This is useful as a reference here and code completion in IDEs
 * @type {Object}
 * @property {function(): Promise<string|null>} getAccessToken - Returns the access token
 * @property {function(): Promise<string|null>} getTokenType - Returns the access token type
 * @property {function(): Promise<boolean>} isAuthenticated - Returns authentication status
 * @property {function(): Promise<string|null>} renewToken - Renews the access token
 * @property {string|null} mode - The authentication mode (e.g., "test" for testing purposes)
 */
const authInterface = {
  getAccessToken: () => Promise.resolve(null),
  getTokenType: () => Promise.resolve(null),
  isAuthenticated: () => Promise.resolve(false),
  mode: null,
  renewToken: () => Promise.resolve(null)
}

/**
 * Factory for CHAPI client methods.
 * @param {Object} auth - Auth interface
 * @param {string} baseUrl - API base URL
 * @param {string} tenant - Tenant identifier
 * @returns {Object} CHAPI client methods
 * @throws {Error} Throws an error if the request fails
 * @example
 * const chapiClient = createChapiClient(auth, 'https://api.example.com', 'my-tenant')
 * chapiClient.getArticleData('article-id', 'order-id')
 * chapiClient.createSupportRequest('order-id', 'Subject', 'Body')
 * chapiClient.getChatSupportToken()
 * chapiClient.getContentConcernForm({ query: 'value' })
 * chapiClient.reportContentConcern({ body: 'Report content concern' })
 */
async function createChapiClient(auth = authInterface, baseUrl = '', tenant = '') {
  const userCookieName = process.env.VUE_APP_JE_AUSER_COOKIE || 'je-auser'
  const jeUserCookie = Cookies.get(userCookieName)
  const jeDeviceId = generateJeDeviceId(jeUserCookie)
  const token = await auth.getAccessToken()
  const tokenType = await auth.getTokenType()

  /**
   * Creates an Axios instance with appropriate headers.
   * @param {string} [authHeaderValue] - Optional Authorization header value
   * @returns {import("axios").AxiosInstance}
   * @throws {Error} Throws an error if the request fails
   */
  function createAxios(authHeaderValue) {
    const headers = {
      Accept: 'application/json',
      'Accept-Version': process.env.VUE_APP_CHAPI_VERSION,
      'Content-type': 'application/json',
      'x-je-device-id': jeDeviceId,
      'x-je-client-type': 'Web',
      'x-je-client-version': '1'
    }
    if (authHeaderValue) headers.Authorization = authHeaderValue
    return axios.create({ baseURL: baseUrl, headers })
  }

  /**
   * Returns an Axios client, renewing token if needed.
   * @param {boolean} [forceRenew=false] - Force token renewal
   * @returns {Promise<import("axios").AxiosInstance>}
   * @throws {Error} Throws an error if the request fails
   */
  async function apiClient(forceRenew = false) {
    let _token = token || await auth.getAccessToken()
    const _tokenType = tokenType || await auth.getTokenType()

    // [Case] Force renew or Expired Token
    if (forceRenew) {
      _token = await auth.renewToken()
    }

    // [Case] No token available, return unauthenticated client
    if (!_token) {
      return createAxios()
    }

    // [Case] Token available, return authenticated client
    return createAxios(`${_tokenType} ${_token}`)
  }

  /**
   * [GET] Retrieve Article Data from CHAPI
   *  - Order Id helps CHAPI performance
   * @param {String} articleId Article Id
   * @param {String} orderId Order Id, could be null when landing directly to Article page
   * @returns {Promise<import("axios").AxiosResponse>} Response data
   * @throws {Error} Throws an error if the request fails
   */
  async function getArticleData(articleId, orderId) {
    const requestQs = orderId ? `?orderId=${orderId}` : ''
    const requestURL = `/applications/consumerhelp/articles/${tenant}/${articleId}${requestQs}`

    try {
      const client = await apiClient()
      return await client.get(requestURL)
    } catch (error) {
      const { response } = error
      if (response && response.status === 401) {
        const client = await apiClient(true)
        return client.request(requestURL)
      }
      throw error
    }
  }

  /**
   * [POST] Creates a Support Request
   * - 401 Unauthorized - Returns the error object
   * @param {String} OrderId property of the support request ticket
   * @param {String} Subject property of the support request ticket
   * @param {String} Body property of the support request ticket
   * @returns {Promise<import("axios").AxiosResponse>}
   * @throws {Error} Throws an error if the request fails
   */
  async function createSupportRequest(OrderId, Subject, Body) {
    const requestURL = `/applications/consumerhelp/support-requests/${tenant}`
    const requestBody = { OrderId, Subject, Body }

    try {
      const client = await apiClient()
      return await client.post(requestURL, requestBody)
    } catch (error) {
      const { response } = error
      if (response && response.status === 401) {
        // 401 - Token expired, re-try with new token
        const client = await apiClient(true)
        return client.post(requestURL, requestBody)
      }
      throw error
    }
  }

  /**
   * [POST] Submits Query Support Request Form with
   * @param {String} OrderId could be empty string
   * @param {String} QueryType selected option query type (<select>)
   * @param {String} Body required user message
   * @returns {Promise<import("axios").AxiosResponse>} response data
   * @throws {Error} Throws an error if the request fails
   */
  async function createQuerySupportRequest(OrderId, QueryType, Body) {
    const requestURL = `/applications/consumerhelp/support-requests/${tenant}`
    const requestBody = { OrderId, QueryType, Body }

    try {
      const client = await apiClient()
      return await client.post(requestURL, requestBody)
    } catch (error) {
      const { response } = error
      if (response && response.status === 401) {
        // 401 - Token expired, re-try with new token
        const client = await apiClient(true)
        return client.post(requestURL, requestBody)
      }
      throw error
    }
  }

  /**
   * [GET] Contains Page Header, Header Icon, Body and Form inputs
   * @param {Object} routeQueries URL query string
   * @returns {Promise<import("axios").AxiosResponse>} Response data
   * @throws {Error} Throws an error if the request fails
   */
  // eslint-disable-next-line no-unused-vars
  async function getContentConcernForm(routeQueries) {
    const queryParams = new URLSearchParams(routeQueries)
    // URLSearchParams.size (Chrome 113+) is not polyfilled reliably; use toString()
    const params = queryParams.toString()
    const queryString = params ? `?${params}` : ''
    const requestURL = `/applications/consumerhelp/support-requests/${tenant}/report-legal-concern${queryString}`

    try {
      const client = await apiClient()
      return await client.get(requestURL)
    } catch (error) {
      window.location = '/help/page-not-found'
      throw error
    }
  }

  /**
   * [POST] Submits Support Request Form data
   * - 400 Invalid Response - Returns the error object
   * @param {String} body ReportContentConcernForm formData
   * @returns {Promise<import("axios").AxiosResponse>} response data
   * @throws {Error} Throws an error if the request fails
   */
  async function reportContentConcern(body) {
    const requestURL = `/applications/consumerhelp/support-requests/${tenant}/report-legal-concern`

    try {
      const client = await apiClient()
      return await client.post(requestURL, body)
    } catch (error) {
      const { response } = error
      if (response && response.status === 401) {
        const client = await apiClient(true)
        return client.post(requestURL, body)
      }
      throw error
    }
  }

  /**
   * [GET] Chat support token from CHAPI
   * @returns {Promise<import("axios").AxiosResponse>}
   * @throws {Error} Throws an error if the request fails
   */
  async function getChatSupportToken() {
    const reqURL = `/applications/consumerhelp/chatsupporttoken/${tenant}`
    try {
      const client = await apiClient()
      return await client.post(reqURL)
    } catch (error) {
      const { response } = error
      if (response && response.status === 401) {
        const client = await apiClient(true)
        return client.post(reqURL)
      }
      throw error
    }
  }

  return {
    createSupportRequest,
    createQuerySupportRequest,
    getArticleData,
    getContentConcernForm,
    getChatSupportToken,
    reportContentConcern
  }
}

export default createChapiClient
